import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { DinamicTitle } from '../utils/DinamicTitle';
import { http, getAuthorizationHeader } from '../config/http';
import { 
  IconSettings, IconMessageCircle, IconDeviceFloppy, 
  IconDatabase, IconFolder, IconPlus, IconEdit, IconTrash, IconX,
  IconBuildingBank, IconCheck, IconQrcode, IconShoppingCartOff, IconBuildingStore,
  IconLock, IconEye, IconEyeOff
} from '@tabler/icons-react';
import QRCode from 'react-qr-code';
import Layouts from '../layouts/Layouts';
import { useMasterData } from '../context/MasterDataContext';
import BankLogo from '../components/BankLogo';
import { validateQRIS, DEFAULT_DEMO_QRIS } from '../utils/qris';
import { 
  DEFAULT_REFUND_ALLOWED_CABANG_IDS, 
  REFUND_SETTINGS_STORAGE_KEY, 
  parseAllowedCabangIds 
} from '../config/refundConfig';

export default function PengaturanSistem() {
  DinamicTitle('Pengaturan Sistem || Komaladewi');

  const [activeTab, setActiveTab] = useState('cepat'); // cepat, configs, gdrive

  return (
    <Layouts>
      <div className="w-full bg-slate-50 dark:bg-slate-900/50 min-h-[85vh] rounded-3xl mt-4 border border-slate-200 dark:border-slate-800 p-4 sm:p-6 lg:p-8">
        {/* Tabs */}
        <div className="flex border-b border-slate-200 dark:border-slate-700 mb-6 space-x-4 overflow-x-auto">
          <button 
            onClick={() => setActiveTab('cepat')}
            className={`py-3 flex items-center gap-2 border-b-2 transition-all whitespace-nowrap ${
              activeTab === 'cepat' 
                ? 'border-amber-500 text-amber-600 dark:text-amber-400 font-bold' 
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <IconSettings size={18} /> Pengaturan Cepat
          </button>
          <button 
            onClick={() => setActiveTab('configs')}
            className={`py-3 flex items-center gap-2 border-b-2 transition-all whitespace-nowrap ${
              activeTab === 'configs' 
                ? 'border-amber-500 text-amber-600 dark:text-amber-400 font-bold' 
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <IconDatabase size={18} /> Semua Konfigurasi
          </button>
          <button 
            onClick={() => setActiveTab('gdrive')}
            className={`py-3 flex items-center gap-2 border-b-2 transition-all whitespace-nowrap ${
              activeTab === 'gdrive' 
                ? 'border-amber-500 text-amber-600 dark:text-amber-400 font-bold' 
                : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <IconFolder size={18} /> Folder Google Drive
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'cepat' && <TabCepat />}
        {activeTab === 'configs' && <TabConfigs />}
        {activeTab === 'gdrive' && <TabGDrive />}

      </div>
    </Layouts>
  );
}

// ==========================================
// COMPONENT: TAB CEPAT
// ==========================================
function TabCepat() {
  const { refreshMasterData, cabangList = [] } = useMasterData();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState({ 
    wa_login_notif: true,
    wa_notif_permohonan_pindah_pusat: true,
    wa_notif_pengiriman_toko: true,
    wa_notif_pengajuan_hapus: true,
    wa_notif_transfer_antar_toko: true,
    wa_notif_verifikasi_pindah_pusat: true,
    wa_notif_verifikasi_pengiriman_toko: true,
    wa_notif_verifikasi_pengajuan_hapus: true,
    wa_notif_verifikasi_transfer_antar_toko: true,
    wa_notif_number: '',
    karat_options: '16,17,18,19,20,21,22,23,24',
    baki_options: '1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20',
    bank_options: 'BCA,BNI,BRI,Mandiri,BSI,Lainnya',
    qris_payload: '',
    refund_allowed_cabang_ids: localStorage.getItem(REFUND_SETTINGS_STORAGE_KEY) || DEFAULT_REFUND_ALLOWED_CABANG_IDS.join(','),
    owner_pin: '123456',
    store_pins: {}
  });

  const [showOwnerPin, setShowOwnerPin] = useState(false);
  const [showStorePinMap, setShowStorePinMap] = useState({});
  const [bulkStorePin, setBulkStorePin] = useState('');

  const [customBankInput, setCustomBankInput] = useState('');
  const [showRawBankInput, setShowRawBankInput] = useState(false);

  // Filter daftar toko untuk pengaturan akses menu refund & PIN Toko
  const tokoBranches = cabangList.filter((c) => (c.is_toko === 1 || c.is_toko === '1' || c.is_toko === true) && !c.nama_cabang?.toLowerCase().includes('brankas'));
  const activeTokoList = tokoBranches.length > 0 ? tokoBranches : cabangList.filter((c) => !c.nama_cabang?.toLowerCase().includes('brankas'));

  const currentAllowedRefundIds = parseAllowedCabangIds(settings.refund_allowed_cabang_ids);

  const toggleCabangRefund = (cabangId) => {
    const strId = String(cabangId).trim();
    let updated;
    if (currentAllowedRefundIds.includes(strId)) {
      updated = currentAllowedRefundIds.filter((id) => id !== strId);
    } else {
      updated = [...currentAllowedRefundIds, strId];
    }
    setSettings((prev) => ({ ...prev, refund_allowed_cabang_ids: updated.join(',') }));
  };

  const handleSelectAllRefund = () => {
    const allIds = activeTokoList.map((c) => String(c.id).trim());
    setSettings((prev) => ({ ...prev, refund_allowed_cabang_ids: allIds.join(',') }));
  };

  const handleDeselectAllRefund = () => {
    setSettings((prev) => ({ ...prev, refund_allowed_cabang_ids: '' }));
  };

  const handleResetDefaultRefund = () => {
    setSettings((prev) => ({
      ...prev,
      refund_allowed_cabang_ids: DEFAULT_REFUND_ALLOWED_CABANG_IDS.join(','),
    }));
    toast.info('Pengaturan akses refund dikembalikan ke default template', { autoClose: 1000 });
  };

  const POPULAR_BANKS = [
    'BCA',
    'Mandiri',
    'BRI',
    'BNI',
    'BSI',
    'CIMB Niaga',
    'Permata',
    'Danamon',
    'BTN',
    'Panin',
    'Bank Jago',
    'SeaBank'
  ];

  const currentBankList = (settings.bank_options || '')
    .split(',')
    .map((b) => b.trim())
    .filter(Boolean);

  const toggleBank = (bankName) => {
    const exists = currentBankList.some(
      (b) => b.toLowerCase() === bankName.toLowerCase()
    );
    let updated;
    if (exists) {
      updated = currentBankList.filter(
        (b) => b.toLowerCase() !== bankName.toLowerCase()
      );
    } else {
      updated = [...currentBankList, bankName];
    }
    setSettings((prev) => ({ ...prev, bank_options: updated.join(',') }));
  };

  const handleAddCustomBank = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!customBankInput.trim()) return;
    const name = customBankInput.trim();
    if (currentBankList.some((b) => b.toLowerCase() === name.toLowerCase())) {
      toast.info(`Bank ${name} sudah ada di daftar aktif`);
      return;
    }
    const updated = [...currentBankList, name];
    setSettings((prev) => ({ ...prev, bank_options: updated.join(',') }));
    setCustomBankInput('');
  };

  const handleRemoveBank = (bankName) => {
    const updated = currentBankList.filter(
      (b) => b.toLowerCase() !== bankName.toLowerCase()
    );
    setSettings((prev) => ({ ...prev, bank_options: updated.join(',') }));
  };

  const handleApplyBulkStorePin = () => {
    if (!bulkStorePin || bulkStorePin.length < 4) {
      toast.warning('Masukkan minimal 4-6 digit angka untuk disamakan ke semua toko', { autoClose: 1800 });
      return;
    }
    const newStorePins = {};
    activeTokoList.forEach((t) => {
      newStorePins[String(t.id)] = bulkStorePin;
    });
    setSettings((prev) => ({
      ...prev,
      store_pins: {
        ...(prev.store_pins || {}),
        ...newStorePins,
      },
    }));
    toast.info(`PIN untuk ${activeTokoList.length} toko berhasil disamakan ke ${bulkStorePin}`, { autoClose: 1200 });
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  async function fetchSettings() {
    setIsLoading(true);
    try {
      const res = await http.get('/settings', { headers: { Authorization: getAuthorizationHeader() } });
      if (res.data && res.data.data) {
        const d = res.data.data;
        const savedRefund = (d.refund_allowed_cabang_ids !== undefined && d.refund_allowed_cabang_ids !== null)
          ? String(d.refund_allowed_cabang_ids)
          : (localStorage.getItem(REFUND_SETTINGS_STORAGE_KEY) !== null
              ? localStorage.getItem(REFUND_SETTINGS_STORAGE_KEY)
              : DEFAULT_REFUND_ALLOWED_CABANG_IDS.join(','));

        setSettings({ 
          wa_login_notif: d.wa_login_notif === 1 || d.wa_login_notif === true,
          wa_notif_permohonan_pindah_pusat: d.wa_notif_permohonan_pindah_pusat === 1 || d.wa_notif_permohonan_pindah_pusat === true,
          wa_notif_pengiriman_toko: d.wa_notif_pengiriman_toko === 1 || d.wa_notif_pengiriman_toko === true,
          wa_notif_pengajuan_hapus: d.wa_notif_pengajuan_hapus === 1 || d.wa_notif_pengajuan_hapus === true,
          wa_notif_transfer_antar_toko: d.wa_notif_transfer_antar_toko === 1 || d.wa_notif_transfer_antar_toko === true,
          wa_notif_verifikasi_pindah_pusat: d.wa_notif_verifikasi_pindah_pusat === 1 || d.wa_notif_verifikasi_pindah_pusat === true,
          wa_notif_verifikasi_pengiriman_toko: d.wa_notif_verifikasi_pengiriman_toko === 1 || d.wa_notif_verifikasi_pengiriman_toko === true,
          wa_notif_verifikasi_pengajuan_hapus: d.wa_notif_verifikasi_pengajuan_hapus === 1 || d.wa_notif_verifikasi_pengajuan_hapus === true,
          wa_notif_verifikasi_transfer_antar_toko: d.wa_notif_verifikasi_transfer_antar_toko === 1 || d.wa_notif_verifikasi_transfer_antar_toko === true,
          wa_notif_number: d.wa_notif_number || '',
          karat_options: d.karat_options || '16,17,18,19,20,21,22,23,24',
          baki_options: d.baki_options || '1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20',
          bank_options: d.bank_options || 'BCA,BNI,BRI,Mandiri,BSI,Lainnya',
          qris_payload: d.qris_payload || localStorage.getItem('komala_qris_payload') || '',
          refund_allowed_cabang_ids: savedRefund,
          owner_pin: d.owner_pin || d.pin_master || '123456',
          store_pins: d.store_pins || {}
        });
      }
    } catch (error) {
      console.warn("Gagal mengambil pengaturan cepat:", error);
    }
    setIsLoading(false);
  }

  async function handleSave(e) {
    e.preventDefault();
    setIsSaving(true);
    try {
      await http.post('/settings', { 
        wa_login_notif: settings.wa_login_notif ? 1 : 0,
        wa_notif_permohonan_pindah_pusat: settings.wa_notif_permohonan_pindah_pusat ? 1 : 0,
        wa_notif_pengiriman_toko: settings.wa_notif_pengiriman_toko ? 1 : 0,
        wa_notif_pengajuan_hapus: settings.wa_notif_pengajuan_hapus ? 1 : 0,
        wa_notif_transfer_antar_toko: settings.wa_notif_transfer_antar_toko ? 1 : 0,
        wa_notif_verifikasi_pindah_pusat: settings.wa_notif_verifikasi_pindah_pusat ? 1 : 0,
        wa_notif_verifikasi_pengiriman_toko: settings.wa_notif_verifikasi_pengiriman_toko ? 1 : 0,
        wa_notif_verifikasi_pengajuan_hapus: settings.wa_notif_verifikasi_pengajuan_hapus ? 1 : 0,
        wa_notif_verifikasi_transfer_antar_toko: settings.wa_notif_verifikasi_transfer_antar_toko ? 1 : 0,
        wa_notif_number: settings.wa_notif_number,
        karat_options: settings.karat_options,
        baki_options: settings.baki_options,
        bank_options: settings.bank_options,
        qris_payload: settings.qris_payload || '',
        refund_allowed_cabang_ids: settings.refund_allowed_cabang_ids || '',
        owner_pin: settings.owner_pin || '123456',
        store_pins: settings.store_pins || {}
      }, { headers: { Authorization: getAuthorizationHeader() } });

      localStorage.setItem('komala_qris_payload', settings.qris_payload || '');
      localStorage.setItem(REFUND_SETTINGS_STORAGE_KEY, settings.refund_allowed_cabang_ids || '');

      if (refreshMasterData) {
        refreshMasterData();
      }
      toast.success('Pengaturan berhasil disimpan!', { autoClose: 1000 });
    } catch (error) {
      console.error(error);
      toast.warning('Gagal menyimpan pengaturan.', { autoClose: 2000 });
    }
    setIsSaving(false);
  }

  if (isLoading) return <div className="animate-pulse h-24 bg-slate-200 dark:bg-slate-800 rounded-2xl w-full"></div>;

  return (
    <form onSubmit={handleSave} className="w-full space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
      {/* 1. NOTIFIKASI WHATSAPP (DINAMIS PER PROSES) - FULL WIDTH */}
      <div className="w-full bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-700">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-slate-100 dark:border-slate-700 gap-2">
          <div>
            <h2 className="text-base font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
              <IconMessageCircle size={20} className="text-green-500" />
              Notifikasi WhatsApp (Dinamis per Proses)
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Atur dan aktifkan/nonaktifkan notifikasi WA kapan saja secara spesifik untuk tiap alur pengajuan.
            </p>
          </div>
        </div>

        {/* Input Nomor WA Penerima Notifikasi */}
        <div className="py-4 border-b border-slate-100 dark:border-slate-700 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex-1">
            <p className="font-semibold text-slate-700 dark:text-slate-300 text-sm">Nomor WhatsApp Penerima Notifikasi</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 max-w-xl">
              Nomor WhatsApp tujuan untuk seluruh notifikasi pengajuan. Pisahkan dengan tanda koma jika lebih dari satu nomor.
            </p>
          </div>
          <div className="w-full sm:w-80 md:w-96 shrink-0">
            <input 
              type="text"
              value={settings.wa_notif_number}
              onChange={(e) => setSettings({ ...settings, wa_notif_number: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-700/50 text-slate-800 dark:text-slate-200 font-mono text-sm focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all"
              placeholder="Contoh: 087750999553"
            />
          </div>
        </div>

        {/* SUB-SECTION A: PENGAJUAN BARU (OPERATOR) */}
        <div className="pt-4 pb-2 border-b border-slate-100 dark:border-slate-700/60">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
              A. Notifikasi Saat Pengajuan Baru Dibuat (Operator)
            </h3>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Pemberitahuan otomatis saat dokumen permohonan baru diajukan dan membutuhkan persetujuan.
          </p>
        </div>

        {/* 1. Pengajuan Pindah ke Brankas Pusat (Operator Rumah) */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-3.5 border-b border-slate-100 dark:border-slate-700">
          <div className="flex-1">
            <p className="font-semibold text-slate-700 dark:text-slate-300 text-sm">
              Pengajuan Pindah ke Brankas Pusat (Operator Rumah)
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Kirim notifikasi WA saat Operator Rumah membuat permohonan pemindahan barang dari Brankas Toko ke Brankas Pusat (<span className="font-mono text-amber-600 dark:text-amber-400">REQ-...</span>).
            </p>
          </div>
          
          <label className="relative inline-flex items-center cursor-pointer shrink-0">
            <input 
              type="checkbox" 
              className="sr-only peer"
              checked={settings.wa_notif_permohonan_pindah_pusat}
              onChange={(e) => setSettings({ ...settings, wa_notif_permohonan_pindah_pusat: e.target.checked })}
            />
            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 dark:peer-focus:ring-amber-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-green-600"></div>
            <span className={`ml-3 text-xs font-bold px-2 py-0.5 rounded-full ${settings.wa_notif_permohonan_pindah_pusat ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'}`}>
              {settings.wa_notif_permohonan_pindah_pusat ? 'Aktif' : 'Nonaktif'}
            </span>
          </label>
        </div>

        {/* 2. Surat Jalan Pengiriman ke Etalase Toko */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-3.5 border-b border-slate-100 dark:border-slate-700">
          <div className="flex-1">
            <p className="font-semibold text-slate-700 dark:text-slate-300 text-sm">
              Surat Jalan Pengiriman ke Etalase Toko (Brankas Pusat ke Toko)
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Kirim notifikasi WA saat membuat Surat Jalan pengiriman barang dari Brankas Pusat ke toko tujuan (<span className="font-mono text-blue-600 dark:text-blue-400">SJ-...</span>).
            </p>
          </div>
          
          <label className="relative inline-flex items-center cursor-pointer shrink-0">
            <input 
              type="checkbox" 
              className="sr-only peer"
              checked={settings.wa_notif_pengiriman_toko}
              onChange={(e) => setSettings({ ...settings, wa_notif_pengiriman_toko: e.target.checked })}
            />
            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 dark:peer-focus:ring-amber-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-green-600"></div>
            <span className={`ml-3 text-xs font-bold px-2 py-0.5 rounded-full ${settings.wa_notif_pengiriman_toko ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'}`}>
              {settings.wa_notif_pengiriman_toko ? 'Aktif' : 'Nonaktif'}
            </span>
          </label>
        </div>

        {/* 3. Pengajuan Penghapusan Barang Toko (Operator Toko) */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-3.5 border-b border-slate-100 dark:border-slate-700">
          <div className="flex-1">
            <p className="font-semibold text-slate-700 dark:text-slate-300 text-sm">
              Pengajuan Penghapusan Barang (Operator Toko)
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Kirim notifikasi WA saat Operator Toko mengajukan penghapusan barang etalase toko (<span className="font-mono text-red-600 dark:text-red-400">DEL-...</span>).
            </p>
          </div>
          
          <label className="relative inline-flex items-center cursor-pointer shrink-0">
            <input 
              type="checkbox" 
              className="sr-only peer"
              checked={settings.wa_notif_pengajuan_hapus}
              onChange={(e) => setSettings({ ...settings, wa_notif_pengajuan_hapus: e.target.checked })}
            />
            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 dark:peer-focus:ring-amber-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-green-600"></div>
            <span className={`ml-3 text-xs font-bold px-2 py-0.5 rounded-full ${settings.wa_notif_pengajuan_hapus ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'}`}>
              {settings.wa_notif_pengajuan_hapus ? 'Aktif' : 'Nonaktif'}
            </span>
          </label>
        </div>

        {/* 4. Pengajuan Transfer Antar Toko (Operator Toko) */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-3.5 border-b border-slate-100 dark:border-slate-700">
          <div className="flex-1">
            <p className="font-semibold text-slate-700 dark:text-slate-300 text-sm">
              Pengajuan Transfer Antar Toko (Operator Toko)
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Kirim notifikasi WA saat Operator Toko mengajukan perpindahan barang antar cabang toko (<span className="font-mono text-purple-600 dark:text-purple-400">TAT-...</span>).
            </p>
          </div>
          
          <label className="relative inline-flex items-center cursor-pointer shrink-0">
            <input 
              type="checkbox" 
              className="sr-only peer"
              checked={settings.wa_notif_transfer_antar_toko}
              onChange={(e) => setSettings({ ...settings, wa_notif_transfer_antar_toko: e.target.checked })}
            />
            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 dark:peer-focus:ring-amber-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-green-600"></div>
            <span className={`ml-3 text-xs font-bold px-2 py-0.5 rounded-full ${settings.wa_notif_transfer_antar_toko ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'}`}>
              {settings.wa_notif_transfer_antar_toko ? 'Aktif' : 'Nonaktif'}
            </span>
          </label>
        </div>

        {/* SUB-SECTION B: HASIL VERIFIKASI / PERSETUJUAN (OWNER / VERIFIKATOR) */}
        <div className="pt-6 pb-2 border-b border-slate-100 dark:border-slate-700/60">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
              B. Notifikasi Saat Pengajuan Diverifikasi / Disetujui / Ditolak (Owner / Verifikator)
            </h3>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Pemberitahuan otomatis saat dokumen selesai diverifikasi, disetujui, atau ditolak beserta alasannya.
          </p>
        </div>

        {/* 5. Hasil Verifikasi Pindah ke Brankas Pusat */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-3.5 border-b border-slate-100 dark:border-slate-700">
          <div className="flex-1">
            <p className="font-semibold text-slate-700 dark:text-slate-300 text-sm">
              Hasil Verifikasi Pindah ke Brankas Pusat
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Kirim notifikasi WA saat dokumen permohonan brankas (<span className="font-mono text-amber-600 dark:text-amber-400">REQ-...</span>) disetujui atau ditolak oleh Verifikator Pusat.
            </p>
          </div>
          
          <label className="relative inline-flex items-center cursor-pointer shrink-0">
            <input 
              type="checkbox" 
              className="sr-only peer"
              checked={settings.wa_notif_verifikasi_pindah_pusat}
              onChange={(e) => setSettings({ ...settings, wa_notif_verifikasi_pindah_pusat: e.target.checked })}
            />
            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 dark:peer-focus:ring-amber-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-green-600"></div>
            <span className={`ml-3 text-xs font-bold px-2 py-0.5 rounded-full ${settings.wa_notif_verifikasi_pindah_pusat ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'}`}>
              {settings.wa_notif_verifikasi_pindah_pusat ? 'Aktif' : 'Nonaktif'}
            </span>
          </label>
        </div>

        {/* 6. Hasil Verifikasi Pengiriman / Surat Jalan Toko */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-3.5 border-b border-slate-100 dark:border-slate-700">
          <div className="flex-1">
            <p className="font-semibold text-slate-700 dark:text-slate-300 text-sm">
              Hasil Verifikasi Surat Jalan Pengiriman ke Toko
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Kirim notifikasi WA saat Surat Jalan pengiriman (<span className="font-mono text-blue-600 dark:text-blue-400">SJ-...</span>) diverifikasi / diterima atau ditolak oleh Toko tujuan.
            </p>
          </div>
          
          <label className="relative inline-flex items-center cursor-pointer shrink-0">
            <input 
              type="checkbox" 
              className="sr-only peer"
              checked={settings.wa_notif_verifikasi_pengiriman_toko}
              onChange={(e) => setSettings({ ...settings, wa_notif_verifikasi_pengiriman_toko: e.target.checked })}
            />
            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 dark:peer-focus:ring-amber-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-green-600"></div>
            <span className={`ml-3 text-xs font-bold px-2 py-0.5 rounded-full ${settings.wa_notif_verifikasi_pengiriman_toko ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'}`}>
              {settings.wa_notif_verifikasi_pengiriman_toko ? 'Aktif' : 'Nonaktif'}
            </span>
          </label>
        </div>

        {/* 7. Hasil Persetujuan Hapus Barang Etalase */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-3.5 border-b border-slate-100 dark:border-slate-700">
          <div className="flex-1">
            <p className="font-semibold text-slate-700 dark:text-slate-300 text-sm">
              Hasil Persetujuan Penghapusan Barang Etalase
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Kirim notifikasi WA saat pengajuan hapus barang (<span className="font-mono text-red-600 dark:text-red-400">DEL-...</span>) disetujui atau ditolak oleh Owner/Verifikator.
            </p>
          </div>
          
          <label className="relative inline-flex items-center cursor-pointer shrink-0">
            <input 
              type="checkbox" 
              className="sr-only peer"
              checked={settings.wa_notif_verifikasi_pengajuan_hapus}
              onChange={(e) => setSettings({ ...settings, wa_notif_verifikasi_pengajuan_hapus: e.target.checked })}
            />
            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 dark:peer-focus:ring-amber-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-green-600"></div>
            <span className={`ml-3 text-xs font-bold px-2 py-0.5 rounded-full ${settings.wa_notif_verifikasi_pengajuan_hapus ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'}`}>
              {settings.wa_notif_verifikasi_pengajuan_hapus ? 'Aktif' : 'Nonaktif'}
            </span>
          </label>
        </div>

        {/* 8. Hasil Verifikasi Transfer Antar Toko */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-3.5 border-b border-slate-100 dark:border-slate-700">
          <div className="flex-1">
            <p className="font-semibold text-slate-700 dark:text-slate-300 text-sm">
              Hasil Verifikasi Transfer Antar Toko
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Kirim notifikasi WA saat pengajuan transfer barang antar toko (<span className="font-mono text-purple-600 dark:text-purple-400">TAT-...</span>) disetujui atau ditolak oleh Verifikator.
            </p>
          </div>
          
          <label className="relative inline-flex items-center cursor-pointer shrink-0">
            <input 
              type="checkbox" 
              className="sr-only peer"
              checked={settings.wa_notif_verifikasi_transfer_antar_toko}
              onChange={(e) => setSettings({ ...settings, wa_notif_verifikasi_transfer_antar_toko: e.target.checked })}
            />
            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 dark:peer-focus:ring-amber-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-green-600"></div>
            <span className={`ml-3 text-xs font-bold px-2 py-0.5 rounded-full ${settings.wa_notif_verifikasi_transfer_antar_toko ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'}`}>
              {settings.wa_notif_verifikasi_transfer_antar_toko ? 'Aktif' : 'Nonaktif'}
            </span>
          </label>
        </div>

        {/* SUB-SECTION C: NOTIFIKASI SISTEM LAINNYA */}
        <div className="pt-6 pb-2 border-b border-slate-100 dark:border-slate-700/60">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="w-2.5 h-2.5 rounded-full bg-slate-500"></span>
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
              C. Notifikasi Sistem Lainnya
            </h3>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Notifikasi terkait aktivitas operasional login user.
          </p>
        </div>

        {/* 9. Kirim WA Saat Karyawan Login */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-3.5">
          <div className="flex-1">
            <p className="font-semibold text-slate-700 dark:text-slate-300 text-sm">
              Kirim WA Saat Karyawan Login
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Kirim notifikasi WA ke Owner setiap kali ada karyawan yang berhasil login ke aplikasi.
            </p>
          </div>
          
          <label className="relative inline-flex items-center cursor-pointer shrink-0">
            <input 
              type="checkbox" 
              className="sr-only peer"
              checked={settings.wa_login_notif}
              onChange={(e) => setSettings({ ...settings, wa_login_notif: e.target.checked })}
            />
            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 dark:peer-focus:ring-amber-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-green-600"></div>
            <span className={`ml-3 text-xs font-bold px-2 py-0.5 rounded-full ${settings.wa_login_notif ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-slate-100 dark:bg-slate-700 text-slate-500'}`}>
              {settings.wa_login_notif ? 'Aktif' : 'Nonaktif'}
            </span>
          </label>
        </div>
      </div>

      {/* PENGATURAN PIN KEAMANAN (OWNER & SETIAP TOKO) */}
      <div className="w-full bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-700">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4 border-b border-slate-100 dark:border-slate-700/60 pb-3">
          <h2 className="text-base font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
            <IconLock size={20} className="text-amber-500" />
            Pengaturan PIN Keamanan (Owner & Toko)
          </h2>
          <span className="text-xs bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2.5 py-1 rounded-full font-medium border border-amber-200 dark:border-amber-800/40 w-fit">
            Otorisasi Transaksi & Fitur Sensitif
          </span>
        </div>

        <p className="text-xs text-slate-500 dark:text-slate-400 mb-6 leading-relaxed">
          Atur PIN khusus untuk akun <strong className="text-slate-700 dark:text-slate-300">Owner</strong> sebagai Master Key sistem, serta PIN untuk masing-masing <strong className="text-slate-700 dark:text-slate-300">Toko</strong> yang berlaku bagi seluruh level akun (kasir, operator, kepala toko) di cabang tersebut.
        </p>

        {/* SECTION A: PIN KHUSUS AKUN OWNER */}
        <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-amber-50/70 via-amber-50/40 to-slate-50/50 dark:from-amber-950/20 dark:via-slate-800 dark:to-slate-800 border border-amber-200/80 dark:border-amber-700/40 mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="max-w-xl">
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-0.5 text-[11px] font-bold rounded-md bg-amber-500 text-white uppercase tracking-wider">
                  Master Key
                </span>
                <h3 className="font-bold text-slate-800 dark:text-slate-200 text-sm sm:text-base">
                  PIN Khusus Akun Owner (Super Admin)
                </h3>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                PIN ini hanya dimiliki oleh akun Owner dan memiliki hak akses bypass tertinggi untuk memverifikasi seluruh aksi sensitif di semua cabang.
              </p>
            </div>

            <div className="w-full md:w-72 shrink-0">
              <div className="relative">
                <input
                  type={showOwnerPin ? 'text' : 'password'}
                  inputMode="numeric"
                  maxLength={12}
                  value={settings.owner_pin || ''}
                  onChange={(e) => setSettings({ ...settings, owner_pin: e.target.value })}
                  placeholder="Contoh: 123456"
                  className="w-full pl-3.5 pr-10 py-2.5 border border-amber-300 dark:border-amber-700/60 rounded-xl bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 font-mono text-base tracking-widest focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-400/20 shadow-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowOwnerPin(!showOwnerPin)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1"
                  title={showOwnerPin ? 'Sembunyikan PIN' : 'Lihat PIN'}
                >
                  {showOwnerPin ? <IconEyeOff size={18} /> : <IconEye size={18} />}
                </button>
              </div>
              <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1">
                Disarankan 4 - 8 digit angka rahasia.
              </p>
            </div>
          </div>
        </div>

        {/* SECTION B: PIN SETIAP TOKO */}
        <div className="border-t border-slate-100 dark:border-slate-700/70 pt-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                <h3 className="font-bold text-slate-800 dark:text-slate-200 text-sm sm:text-base">
                  PIN Setiap Toko
                </h3>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                PIN cabang yang dapat digunakan oleh semua level akun di toko tersebut (kasir, operator, kepala toko).
              </p>
            </div>

            {/* Quick Bulk Action */}
            <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-700/40 p-1.5 rounded-xl border border-slate-200 dark:border-slate-700 shrink-0">
              <input
                type="text"
                inputMode="numeric"
                maxLength={8}
                value={bulkStorePin}
                onChange={(e) => setBulkStorePin(e.target.value)}
                placeholder="PIN sama..."
                className="w-24 sm:w-28 px-2.5 py-1.5 text-xs font-mono rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-amber-500"
              />
              <button
                type="button"
                onClick={handleApplyBulkStorePin}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-200 hover:bg-amber-500 hover:text-white dark:bg-slate-600 dark:hover:bg-amber-500 text-slate-700 dark:text-slate-200 transition-colors whitespace-nowrap"
                title="Terapkan PIN ini ke semua cabang toko sekaligus"
              >
                Samakan Semua Toko
              </button>
            </div>
          </div>

          {activeTokoList.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-400 border border-dashed border-slate-200 dark:border-slate-700 rounded-xl">
              Tidak ada data cabang toko yang ditemukan.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5 mt-3">
              {activeTokoList.map((toko) => {
                const storeIdStr = String(toko.id);
                const currentVal = settings.store_pins?.[storeIdStr] || '';
                const isVisible = !!showStorePinMap[storeIdStr];

                return (
                  <div 
                    key={toko.id} 
                    className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-700/70 bg-slate-50/50 dark:bg-slate-800/40 hover:border-slate-300 dark:hover:border-slate-600 transition-all flex flex-col justify-between gap-2.5"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 truncate">
                        <IconBuildingStore size={18} className="text-blue-500 shrink-0" />
                        <span className="font-semibold text-slate-700 dark:text-slate-200 text-sm truncate" title={toko.nama_cabang}>
                          {toko.nama_cabang}
                        </span>
                      </div>
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 shrink-0">
                        ID: {toko.id}
                      </span>
                    </div>

                    <div className="relative">
                      <input
                        type={isVisible ? 'text' : 'password'}
                        inputMode="numeric"
                        maxLength={12}
                        value={currentVal}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSettings((prev) => ({
                            ...prev,
                            store_pins: {
                              ...(prev.store_pins || {}),
                              [storeIdStr]: val,
                            },
                          }));
                        }}
                        placeholder="PIN Toko (misal 112233)"
                        className="w-full pl-3 pr-9 py-2 text-sm font-mono tracking-wider rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setShowStorePinMap((prev) => ({
                            ...prev,
                            [storeIdStr]: !prev[storeIdStr],
                          }));
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1"
                        title={isVisible ? 'Sembunyikan' : 'Lihat'}
                      >
                        {isVisible ? <IconEyeOff size={16} /> : <IconEye size={16} />}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* 2. PILIHAN BAKI (DINAMIS) - FULL WIDTH */}
      <div className="w-full bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-700">
        <h2 className="text-base font-bold text-slate-800 dark:text-slate-200 mb-4 flex items-center gap-2">
          <IconSettings size={20} className="text-emerald-500" />
          Pilihan Baki (Dinamis)
        </h2>
        
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 py-3 border-t border-slate-100 dark:border-slate-700">
          <div className="flex-1">
            <p className="font-semibold text-slate-700 dark:text-slate-300">Daftar Pilihan Baki</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-xl">
              Tentukan angka-angka baki yang ingin ditampilkan. Pisahkan dengan tanda koma (contoh: 1,2,3,4,5).
            </p>
          </div>
          
          <div className="w-full sm:w-80 md:w-96 shrink-0">
            <input 
              type="text"
              value={settings.baki_options}
              onChange={(e) => setSettings({ ...settings, baki_options: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-700/50 text-slate-800 dark:text-slate-200 font-mono text-sm focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
              placeholder="Contoh: 1,2,3,4,5,6,7,8,9,10"
            />
          </div>
        </div>
      </div>

      {/* 3. PILIHAN KARAT (DINAMIS) - FULL WIDTH */}
      <div className="w-full bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-700">
        <h2 className="text-base font-bold text-slate-800 dark:text-slate-200 mb-4 flex items-center gap-2">
          <IconSettings size={20} className="text-amber-500" />
          Pilihan Karat (Dinamis)
        </h2>
        
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 py-3 border-t border-slate-100 dark:border-slate-700">
          <div className="flex-1">
            <p className="font-semibold text-slate-700 dark:text-slate-300">Daftar Pilihan Karat</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-xl">
              Tentukan angka-angka karat yang ingin ditampilkan di seluruh aplikasi. Pisahkan dengan tanda koma (contoh: 16,17,18,20,24).
            </p>
          </div>
          
          <div className="w-full sm:w-80 md:w-96 shrink-0">
            <input 
              type="text"
              value={settings.karat_options}
              onChange={(e) => setSettings({ ...settings, karat_options: e.target.value })}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-700/50 text-slate-800 dark:text-slate-200 font-mono text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-all"
              placeholder="Contoh: 16,17,18,19,20,21,22,23,24"
            />
          </div>
        </div>
      </div>

      {/* 4. PILIHAN BANK & MESIN EDC TOKO (SETUP DINAMIS) - FULL WIDTH */}
      <div className="w-full bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-700 space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 dark:border-slate-700 pb-3">
          <div>
            <h2 className="text-base font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
              <IconBuildingBank size={20} className="text-blue-500" />
              Pilihan Bank & Mesin EDC Toko (Setup Dinamis)
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Pilih bank resmi yang aktif digunakan untuk transaksi transfer dan mesin EDC di kasir
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowRawBankInput(!showRawBankInput)}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
          >
            {showRawBankInput ? 'Tutup Input Manual' : 'Mode Teks Manual'}
          </button>
        </div>

        {/* 1. KATALOG BANK RESMI INDONESIA (KLIK UNTUK AKTIFKAN / NONAKTIFKAN) */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs font-bold text-slate-700 dark:text-slate-300">
            <span>Katalog Bank Resmi (Klik untuk Mengaktifkan/Menonaktifkan):</span>
            <span className="text-[11px] text-slate-400">Klik logo bank untuk toggle</span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2.5">
            {POPULAR_BANKS.map((bankName) => {
              const isActive = currentBankList.some(
                (b) => b.toLowerCase() === bankName.toLowerCase()
              );
              return (
                <button
                  key={bankName}
                  type="button"
                  onClick={() => toggleBank(bankName)}
                  className={`p-2.5 rounded-xl border transition-all text-left flex flex-col justify-between gap-2.5 cursor-pointer ${
                    isActive
                      ? 'bg-blue-50/70 dark:bg-blue-950/40 border-blue-500 ring-2 ring-blue-500/20 shadow-xs'
                      : 'bg-slate-50/60 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700 hover:border-slate-300 opacity-60 hover:opacity-100'
                  }`}
                >
                  <div className="flex items-center justify-between w-full">
                    <BankLogo name={bankName} size="sm" />
                    {isActive ? (
                      <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0">
                        <IconCheck size={13} stroke={3} />
                      </span>
                    ) : (
                      <span className="w-5 h-5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-400 flex items-center justify-center shrink-0">
                        <IconPlus size={13} stroke={2.5} />
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-xs font-bold">
                    <span className="text-slate-800 dark:text-slate-100">{bankName}</span>
                    <span className={`text-[10px] ${isActive ? 'text-emerald-600 font-bold' : 'text-slate-400'}`}>
                      {isActive ? 'Aktif' : 'Mati'}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* 2. TAMBAH BANK KUSTOM / BANK DAERAH */}
        <div className="pt-2 border-t border-slate-100 dark:border-slate-700">
          <div className="text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
            Tambah Bank Kustom / Bank Daerah:
          </div>
          <div className="flex items-center gap-2 max-w-xl">
            <input
              type="text"
              value={customBankInput}
              onChange={(e) => setCustomBankInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddCustomBank(e);
                }
              }}
              placeholder="Contoh: Bank BPD NTB Syariah, BJB, Bank Jatim..."
              className="flex-1 px-3.5 py-2 text-xs border border-slate-300 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-700/50 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-blue-500"
            />
            <button
              type="button"
              onClick={handleAddCustomBank}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl transition flex items-center gap-1 cursor-pointer shadow-xs"
            >
              <IconPlus size={15} />
              <span>Tambah</span>
            </button>
          </div>
        </div>

        {/* 3. DAFTAR BANK YANG SAAT INI AKTIF DI KASIR */}
        <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-900/40 border border-slate-200/80 dark:border-slate-700/70 space-y-2">
          <div className="flex items-center justify-between text-xs font-bold text-slate-700 dark:text-slate-300">
            <span className="flex items-center gap-1.5">
              <span>Daftar Bank Aktif di Kasir ({currentBankList.length} Bank Aktif)</span>
            </span>
            <span className="text-[11px] text-slate-400">Klik tanda silang (x) untuk menonaktifkan</span>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            {currentBankList.length === 0 ? (
              <span className="text-xs italic text-slate-400 py-1">Belum ada bank yang aktif. Silahkan pilih bank pada katalog di atas.</span>
            ) : (
              currentBankList.map((bankName) => (
                <div
                  key={bankName}
                  className="px-2.5 py-1.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xs flex items-center gap-2 group"
                >
                  <BankLogo name={bankName} size="xs" />
                  <span className="text-xs font-bold text-slate-800 dark:text-slate-200">{bankName}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveBank(bankName)}
                    className="p-0.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-full transition cursor-pointer"
                    title={`Nonaktifkan ${bankName}`}
                  >
                    <IconX size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* MODE MANUAL / RAW TEXT (Bisa di-toggle jika dibutuhkan) */}
        {showRawBankInput && (
          <div className="p-3 bg-amber-50/70 dark:bg-amber-950/30 rounded-xl border border-amber-200 dark:border-amber-900/50 space-y-1.5">
            <span className="text-xs font-bold text-amber-800 dark:text-amber-300">
              Input String Teks Mentah (Pisahkan dengan tanda koma):
            </span>
            <input
              type="text"
              value={settings.bank_options}
              onChange={(e) => setSettings({ ...settings, bank_options: e.target.value })}
              className="w-full px-3 py-1.5 border border-amber-300 rounded-lg text-xs font-mono bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100"
            />
          </div>
        )}
      </div>

      {/* 5. PENGATURAN QRIS TOKO (UNTUK QRIS DINAMIS KASIR) - FULL WIDTH */}
      <div className="w-full bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-700 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-slate-100 dark:border-slate-700 gap-2">
          <div>
            <h2 className="text-base font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
              <IconQrcode size={20} className="text-red-500" />
              Pengaturan QRIS Toko (Untuk QRIS Dinamis Kasir)
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Masukkan string QRIS Statis toko (dari stiker QRIS atau merchant bank). Kasir akan otomatis mengubahnya menjadi QRIS Dinamis dengan nominal transaksi terkunci.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {validateQRIS(settings.qris_payload) ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800">
                <IconCheck size={14} stroke={3} />
                QRIS Valid & Aktif
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-800">
                Menggunakan Mode Demo
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pt-2">
          {/* Kolom Kiri: Input dan Tombol Aksi */}
          <div className="lg:col-span-2 space-y-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                Payload String QRIS Statis Toko:
              </label>
              <textarea
                rows={4}
                value={settings.qris_payload}
                onChange={(e) => setSettings({ ...settings, qris_payload: e.target.value.trim() })}
                placeholder="Tempelkan string QRIS di sini, contoh: 00020101021126580014ID.LINKAJA.WWW..."
                className="w-full px-3.5 py-2.5 text-xs font-mono border border-slate-300 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-700/50 text-slate-800 dark:text-slate-200 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <button
                type="button"
                onClick={() => setSettings({ ...settings, qris_payload: DEFAULT_DEMO_QRIS })}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 transition"
              >
                Gunakan Contoh Demo Valid
              </button>
              {settings.qris_payload && (
                <button
                  type="button"
                  onClick={() => setSettings({ ...settings, qris_payload: '' })}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-50 hover:bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-400 transition"
                >
                  Kosongkan
                </button>
              )}
            </div>

            {/* Panduan Singkat */}
            <div className="p-3.5 rounded-xl bg-blue-50/60 dark:bg-blue-950/30 border border-blue-200/80 dark:border-blue-900/40 text-xs text-blue-800 dark:text-blue-300 space-y-1">
              <span className="font-bold flex items-center gap-1.5">
                💡 Cara Mendapatkan String QRIS Toko:
              </span>
              <ol className="list-decimal list-inside space-y-0.5 text-[11px] text-blue-700 dark:text-blue-300/90 pl-1">
                <li>Buka aplikasi pemindai barcode / Google Lens di smartphone Anda.</li>
                <li>Arahkan kamera ke stiker QRIS fisik toko Anda (BCA, Mandiri, BRI, ShopeePay, dll).</li>
                <li>Salin (copy) hasil teks pembacaan yang diawali <code className="font-mono bg-blue-100 dark:bg-blue-900/60 px-1 py-0.5 rounded">00020101...</code>.</li>
                <li>Tempelkan (paste) ke kotak di atas lalu klik tombol <strong>Simpan Pengaturan</strong> di bawah.</li>
              </ol>
            </div>
          </div>

          {/* Kolom Kanan: Live Preview Barcode */}
          <div className="flex flex-col items-center justify-center p-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-700 text-center">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
              Preview Barcode Master Toko
            </span>
            <div className="p-3 bg-white rounded-xl shadow-xs border border-slate-200 flex items-center justify-center">
              <QRCode
                value={settings.qris_payload || DEFAULT_DEMO_QRIS}
                size={120}
                style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                viewBox="0 0 256 256"
              />
            </div>
            <span className="text-[10px] text-slate-500 dark:text-slate-400 mt-2 font-medium">
              {validateQRIS(settings.qris_payload)
                ? 'QRIS Toko Terbaca Sempurna'
                : 'Preview Template QRIS Demo'}
            </span>
          </div>
        </div>
      </div>

      {/* 6. HAK AKSES MENU PENGEMBALIAN / RETURN TOKO (SETUP DINAMIS & FALLBACK) - FULL WIDTH */}
      <div className="w-full bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-700 space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-700 pb-3">
          <div>
            <h2 className="text-base font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
              <IconShoppingCartOff size={20} className="text-amber-500" />
              Akses Menu Pengembalian / Return Toko (Dinamis & Fallback)
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Pilih toko mana saja yang kasirnya diperbolehkan mengakses menu Pengembalian. Toko yang tidak diizinkan otomatis tidak akan melihat menu ini.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleSelectAllRefund}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200/60 dark:border-amber-800/40 transition"
            >
              Aktifkan Semua
            </button>
            <button
              type="button"
              onClick={handleDeselectAllRefund}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 transition"
            >
              Nonaktifkan Semua
            </button>
            <button
              type="button"
              onClick={handleResetDefaultRefund}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200/60 dark:border-blue-800/40 transition"
              title="Kembalikan ke template default frontend"
            >
              Reset ke Default
            </button>
          </div>
        </div>

        {/* Info Box */}
        <div className="p-3.5 rounded-xl bg-amber-50/60 dark:bg-amber-950/30 border border-amber-200/80 dark:border-amber-900/40 text-xs text-amber-800 dark:text-amber-300 space-y-1">
          <span className="font-bold flex items-center gap-1.5">
            ℹ️ Catatan Hak Akses:
          </span>
          <p className="text-[11px] text-amber-700 dark:text-amber-300/90 leading-relaxed">
            • <strong>Owner & Admin</strong> selalu memiliki akses ke menu Pengembalian tanpa batasan toko.<br />
            • <strong>Kasir Penjualan & Kasir Pembelian</strong> hanya dapat mengakses menu Pengembalian jika toko aktifnya dicentang/aktif di bawah ini.<br />
            • Jika data pengaturan backend belum terisi, sistem otomatis menerapkan template default frontend (Opsi 3).
          </p>
        </div>

        {/* Branch Cards */}
        {activeTokoList.length === 0 ? (
          <div className="py-6 text-center text-xs text-slate-400">
            Tidak ada data cabang toko yang ditemukan.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {activeTokoList.map((cb) => {
              const strId = String(cb.id).trim();
              const isAllowed = currentAllowedRefundIds.includes(strId);
              return (
                <div
                  key={cb.id}
                  onClick={() => toggleCabangRefund(cb.id)}
                  className={`p-3.5 rounded-xl border transition-all cursor-pointer flex items-center justify-between gap-3 ${
                    isAllowed
                      ? 'bg-amber-50/70 dark:bg-amber-950/30 border-amber-300 dark:border-amber-700 shadow-xs'
                      : 'bg-slate-50/60 dark:bg-slate-900/40 border-slate-200 dark:border-slate-700 hover:border-slate-300 opacity-70'
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div
                      className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                        isAllowed
                          ? 'bg-amber-500 text-white shadow-xs'
                          : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                      }`}
                    >
                      <IconBuildingStore size={18} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-xs text-slate-800 dark:text-slate-100 truncate">
                        {cb.nama_cabang || `Cabang ${cb.id}`}
                      </p>
                      <p className="text-[10px] text-slate-400 font-mono">
                        ID: {cb.id}
                      </p>
                    </div>
                  </div>

                  <span
                    className={`shrink-0 px-2.5 py-1 text-[10px] font-bold rounded-full ${
                      isAllowed
                        ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800'
                        : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                    }`}
                  >
                    {isAllowed ? 'Bisa Akses' : 'Terkunci'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex justify-end pt-2">
        <button type="submit" disabled={isSaving} className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-white font-semibold shadow-lg transition-all ${isSaving ? 'bg-slate-400 cursor-not-allowed' : 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 shadow-amber-500/30'}`}>
          {isSaving ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <IconDeviceFloppy size={20} />}
          {isSaving ? 'Menyimpan...' : 'Simpan Pengaturan'}
        </button>
      </div>
    </form>
  );
}

// ==========================================
// COMPONENT: TAB CONFIGS
// ==========================================
function TabConfigs() {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ id: null, key: '', value: '', deskripsi: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => { fetchConfigs(); }, []);

  async function fetchConfigs() {
    setIsLoading(true);
    try {
      const res = await http.get('/settings/configs', { headers: { Authorization: getAuthorizationHeader() } });
      setData(res.data.data || []);
    } catch (err) { toast.error("Gagal mengambil data configs"); }
    setIsLoading(false);
  }

  function handleEdit(item) {
    setFormData({ id: item.id, key: item.key, value: item.value, deskripsi: item.deskripsi || '' });
    setShowModal(true);
  }

  function handleAdd() {
    setFormData({ id: null, key: '', value: '', deskripsi: '' });
    setShowModal(true);
  }

  async function handleDelete(id) {
    if (!window.confirm("Yakin ingin menghapus konfigurasi ini?")) return;
    try {
      await http.delete(`/settings/configs/${id}`, { headers: { Authorization: getAuthorizationHeader() } });
      toast.success("Berhasil dihapus");
      fetchConfigs();
    } catch (err) { toast.error("Gagal menghapus"); }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (formData.id) {
        await http.put(`/settings/configs/${formData.id}`, formData, { headers: { Authorization: getAuthorizationHeader() } });
        toast.success("Berhasil diubah");
      } else {
        await http.post('/settings/configs', formData, { headers: { Authorization: getAuthorizationHeader() } });
        toast.success("Berhasil ditambah");
      }
      setShowModal(false);
      fetchConfigs();
    } catch (err) {
      toast.error(err.response?.data?.message || "Gagal menyimpan data");
    }
    setIsSubmitting(false);
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200">Database Konfigurasi (configs)</h2>
        <button onClick={handleAdd} className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-xl flex items-center gap-2 font-medium">
          <IconPlus size={18} /> Tambah Config
        </button>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                <th className="p-4 text-sm font-semibold text-slate-600 dark:text-slate-300">Key</th>
                <th className="p-4 text-sm font-semibold text-slate-600 dark:text-slate-300">Value</th>
                <th className="p-4 text-sm font-semibold text-slate-600 dark:text-slate-300">Deskripsi</th>
                <th className="p-4 text-sm font-semibold text-slate-600 dark:text-slate-300 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan="4" className="p-4 text-center text-slate-500">Memuat...</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan="4" className="p-4 text-center text-slate-500">Belum ada data config.</td></tr>
              ) : (
                data.map((item) => (
                  <tr key={item.id} className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30">
                    <td className="p-4 font-mono text-sm text-slate-700 dark:text-slate-300">{item.key}</td>
                    <td className="p-4 text-sm text-slate-700 dark:text-slate-300 break-all">{item.value}</td>
                    <td className="p-4 text-sm text-slate-500 dark:text-slate-400">{item.deskripsi || '-'}</td>
                    <td className="p-4 text-right space-x-2 whitespace-nowrap">
                      <button onClick={() => handleEdit(item)} className="p-2 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg"><IconEdit size={18} /></button>
                      <button onClick={() => handleDelete(item.id)} className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"><IconTrash size={18} /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-800 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center">
              <h3 className="font-bold text-lg">{formData.id ? 'Edit Config' : 'Tambah Config'}</h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600"><IconX size={20}/></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Key</label>
                <input required type="text" className="w-full rounded-xl border-slate-200 dark:border-slate-700 dark:bg-slate-900 focus:ring-amber-500 focus:border-amber-500" value={formData.key} onChange={e => setFormData({...formData, key: e.target.value})} disabled={!!formData.id} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Value</label>
                <textarea required rows={3} className="w-full rounded-xl border-slate-200 dark:border-slate-700 dark:bg-slate-900 focus:ring-amber-500 focus:border-amber-500" value={formData.value} onChange={e => setFormData({...formData, value: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Deskripsi (Opsional)</label>
                <input type="text" className="w-full rounded-xl border-slate-200 dark:border-slate-700 dark:bg-slate-900 focus:ring-amber-500 focus:border-amber-500" value={formData.deskripsi} onChange={e => setFormData({...formData, deskripsi: e.target.value})} />
              </div>
              <div className="pt-4 flex justify-end gap-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200">Batal</button>
                <button type="submit" disabled={isSubmitting} className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white flex items-center">{isSubmitting ? 'Loading...' : 'Simpan'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ==========================================
// COMPONENT: TAB GDRIVE
// ==========================================
function TabGDrive() {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ id: null, id_unique: '', bulan: new Date().getMonth() + 1, tahun: new Date().getFullYear() });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  useEffect(() => { fetchFolders(); }, []);

  async function fetchFolders() {
    setIsLoading(true);
    try {
      const res = await http.get('/gdrive-folders', { headers: { Authorization: getAuthorizationHeader() } });
      setData(res.data.data || []);
    } catch (err) { toast.error("Gagal mengambil data folder gdrive"); }
    setIsLoading(false);
  }

  function handleEdit(item) {
    setFormData({ id: item.id, id_unique: item.id_unique, bulan: item.bulan, tahun: item.tahun });
    setShowModal(true);
  }

  function handleAdd() {
    setFormData({ id: null, id_unique: '', bulan: new Date().getMonth() + 1, tahun: new Date().getFullYear() });
    setShowModal(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        id_unique: formData.id_unique,
        bulan: parseInt(formData.bulan),
        tahun: parseInt(formData.tahun)
      };
      
      if (formData.id) {
        await http.put(`/gdrive-folders/${formData.id}`, payload, { headers: { Authorization: getAuthorizationHeader() } });
        toast.success("Berhasil diubah");
      } else {
        await http.post('/gdrive-folders', payload, { headers: { Authorization: getAuthorizationHeader() } });
        toast.success("Berhasil ditambah");
      }
      setShowModal(false);
      fetchFolders();
    } catch (err) {
      toast.error(err.response?.data?.message || "Gagal menyimpan data");
    }
    setIsSubmitting(false);
  }

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = data.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(data.length / rowsPerPage);

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200">Daftar Folder Google Drive</h2>
        <button onClick={handleAdd} className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-xl flex items-center gap-2 font-medium">
          <IconPlus size={18} /> Tambah Folder
        </button>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                <th className="p-4 text-sm font-semibold text-slate-600 dark:text-slate-300">Periode</th>
                <th className="p-4 text-sm font-semibold text-slate-600 dark:text-slate-300">ID Unique (Folder ID)</th>
                <th className="p-4 text-sm font-semibold text-slate-600 dark:text-slate-300">Dibuat Pada</th>
                <th className="p-4 text-sm font-semibold text-slate-600 dark:text-slate-300 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan="4" className="p-4 text-center text-slate-500">Memuat...</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan="4" className="p-4 text-center text-slate-500">Belum ada folder yang didaftarkan.</td></tr>
              ) : (
                currentRows.map((item) => (
                  <tr key={item.id} className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30">
                    <td className="p-4 font-bold text-sm text-slate-800 dark:text-slate-200">{item.label}</td>
                    <td className="p-4 font-mono text-sm text-slate-500 dark:text-slate-400 break-all">{item.id_unique}</td>
                    <td className="p-4 text-sm text-slate-500 dark:text-slate-400">{item.created_at}</td>
                    <td className="p-4 text-right space-x-2 whitespace-nowrap">
                      <button onClick={() => handleEdit(item)} className="p-2 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg"><IconEdit size={18} /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {totalPages > 1 && (
          <div className="flex justify-between items-center p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
            <span className="text-sm text-slate-500 dark:text-slate-400">
              Menampilkan {indexOfFirstRow + 1} - {Math.min(indexOfLastRow, data.length)} dari {data.length} folder
            </span>
            <div className="flex gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
                className="px-3 py-1 text-sm font-medium rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Prev
              </button>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => prev + 1)}
                className="px-3 py-1 text-sm font-medium rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-slate-800 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center">
              <h3 className="font-bold text-lg">{formData.id ? 'Edit Folder' : 'Tambah Folder'}</h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600"><IconX size={20}/></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-1">Bulan</label>
                  <select required className="w-full rounded-xl border-slate-200 dark:border-slate-700 dark:bg-slate-900 focus:ring-amber-500 focus:border-amber-500" value={formData.bulan} onChange={e => setFormData({...formData, bulan: e.target.value})}>
                    {[...Array(12)].map((_, i) => (
                      <option key={i+1} value={i+1}>Bulan {i+1}</option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-1">Tahun</label>
                  <input required type="number" min="2000" className="w-full rounded-xl border-slate-200 dark:border-slate-700 dark:bg-slate-900 focus:ring-amber-500 focus:border-amber-500" value={formData.tahun} onChange={e => setFormData({...formData, tahun: e.target.value})} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Folder ID (Google Drive)</label>
                <input required type="text" className="w-full rounded-xl border-slate-200 dark:border-slate-700 dark:bg-slate-900 focus:ring-amber-500 focus:border-amber-500 font-mono text-sm" value={formData.id_unique} onChange={e => setFormData({...formData, id_unique: e.target.value})} placeholder="Misal: 1A2b3C4d5E..." />
              </div>
              <div className="pt-4 flex justify-end gap-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200">Batal</button>
                <button type="submit" disabled={isSubmitting} className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white flex items-center">{isSubmitting ? 'Loading...' : 'Simpan'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
